/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */
import React, { useState, useEffect, type MouseEvent } from 'react';
import classnames from 'classnames';
import useKeys from 'effects/keys';
import Magnifier from 'components/Icons/Magnifier/Magnifier';

import './Search.scss';

const DEFAULT_LABELS: SearchLabels = {
  searchFor: 'Leit etter:',
  lookingFor: 'Kva ser du etter?',
  close: 'Lukk',
};

const DEFAULT_ON_CLOSE = () => {};

interface SearchLabels {
  searchFor: string;
  lookingFor: string;
  close: string;
}

interface SearchProps {
  action: string;
  labels?: SearchLabels;
  onClose?: () => void;
}

/**
 * This is the Search component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
const Search = ({
  action,
  labels = DEFAULT_LABELS,
  onClose = DEFAULT_ON_CLOSE,
}: SearchProps) => {
  const [visible, setVisible] = useState(false);
  const [focus, setFocus] = useState(false);
  const [text, setText] = useState(false);

  const hide = () => {
    setVisible(false);
  };

  useEffect(() => {
    setVisible(true);
    document.body.classList.add('no-scroll-menu');

    return function cleanup() {
      document.body.classList.remove('no-scroll-menu');
    };
  }, []); // only on mount

  useKeys('search', visible, {
    esc: () => {
      setVisible(false);
    },
  });

  function onTransitionEnd() {
    if (!visible) {
      onClose();
    }
  }

  const onContainerClick = ({ target }: MouseEvent) => {
    if ((target as HTMLElement).nodeName === 'DIV') {
      hide();
    }
  };

  // eslint-disable-next-line
  const renderPlaceholder = () => {
    if (!focus || text) return null;

    return <div className="placeholder">{labels.lookingFor}</div>;
  };

  return (
    <div
      id="search-wrapper"
      className={classnames('search-wrapper', { visible })}
      onTransitionEnd={onTransitionEnd}
      onClick={onContainerClick}
      aria-modal="true"
      role="dialog"
    >
      <form
        role="search"
        method="GET"
        id="searchform"
        className="searchform"
        action={action}
        onClick={e => e.stopPropagation()}
      >
        <label className="screen-reader-text" htmlFor="headerSearch">
          {labels.searchFor}
        </label>
        <div className="search-fields-wrapper">
          <input
            type="text"
            name="s"
            id="headerSearch"
            autoComplete="off"
            autoFocus
            placeholder={labels.lookingFor}
            onChange={({ target: { value } }) => {
              setText(value.length > 0);
            }}
            onFocus={() => {
              setFocus(true);
            }}
            onBlur={() => {
              setFocus(false);
            }}
          />

          <button type="submit">
            <Magnifier />
          </button>

          <button
            type="button"
            className="search-close sr-only"
            onClick={() => onClose()}
          >
            Lukk
          </button>
        </div>
      </form>
    </div>
  );
};

export default Search;
